import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@/shared/lib/utils'
import { Check } from 'lucide-react'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /**
   * Состояние чекбокса
   */
  checked?: boolean
  /**
   * Callback при изменении состояния
   */
  onCheckedChange?: (checked: boolean) => void
  /**
   * Отключённое состояние
   */
  disabled?: boolean
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * Checkbox - премиальный чекбокс для Telegram Mini App
 * 
 * Дизайн:
 * - Мягкие скругления 6px
 * - Плавные анимации
 * - Haptic feedback готов (обрабатывается родителем)
 * - Тёмная тема
 * 
 * @example
 * <Checkbox
 *   id="terms"
 *   checked={agreed}
 *   onCheckedChange={setAgreed}
 * />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ checked, onCheckedChange, disabled, className, id, ...props }, ref) {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        data-state={checked ? 'checked' : 'unchecked'}
        disabled={disabled}
        onClick={() => !disabled && onCheckedChange?.(!checked)}
        className={cn(
          // Base
          'peer inline-flex items-center justify-center shrink-0',
          'h-5 w-5 rounded-md border-2 transition-all duration-150',
          // Focus
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2',
          // States
          checked
            ? 'bg-primary border-primary text-white'
            : 'bg-white dark:bg-dark-card border-gray-300 dark:border-gray-600',
          // Hover
          !disabled && !checked && 'hover:border-primary/50',
          // Disabled
          disabled && 'cursor-not-allowed opacity-50',
          // Active
          !disabled && 'active:scale-95',
          className
        )}
      >
        <Check 
          className={cn(
            'h-3.5 w-3.5 transition-all duration-150',
            checked ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          )} 
          strokeWidth={3}
        />
        {/* Hidden input for form compatibility */}
        <input
          ref={ref}
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
          className="sr-only"
          {...props}
        />
      </button>
    )
  }
)

export default Checkbox

